import { HttpClient } from '@angular/common/http';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '@env/environment';
import { Group, IUser } from 'app/core/models/model.model';
import {
  GeminiError,
  GeminiService,
} from 'app/core/services/gemini/gemini.service';
import { UserService } from 'app/core/services/user/user.service';
import { promptToDownloadBlob } from 'app/utils/file.utils';
import JSZip from 'jszip';
import { Ai } from 'libs/aijs';
import { EAiPosition } from 'libs/aijs/types/ai';
import { firstValueFrom } from 'rxjs';
import { AlertComponent } from '../../shared/components/alert/alert.component';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { LoaderComponent } from '../../shared/components/loader/loader.component';
import {
  GenerationStepComponent,
  PreviewModeOption,
} from '../components/generation-step/generation-step.component';
import { StyleSelectionStepComponent } from '../components/style-selection-step/style-selection-step.component';
import { UploadPreviewStepComponent } from '../components/upload-preview-step/upload-preview-step.component';
import {
  AbPreviewMode,
  createDefaultPhotoConfig,
  EFurnitureStyle,
  ELuxuryLevel,
  FURNITURE_STYLE_OPTIONS,
  PhotoGenerationConfig,
  ProcessedImage,
  WizardAnswers,
} from '../models/wizard.model';
import { WizardShellComponentService } from '../services/wizard-shell-component.service';

type UiState = 'upload' | 'configuring' | 'generation';

@Component({
  selector: 'casa-doc-ai',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AlertComponent,
    LoaderComponent,
    ConfirmDialogComponent,
    UploadPreviewStepComponent,
    StyleSelectionStepComponent,
    GenerationStepComponent,
  ],
  templateUrl: './wizard-shell.component.html',
  styleUrl: './wizard-shell.component.scss',
})
export class WizardShellComponent {
  readonly #initialProcessMaxAttempts = 3;
  readonly #retryDelayMs = 1000;
  /** Massimo numero di immagini elaborate in parallelo da "Genera tutte". */
  readonly #maxConcurrentGenerations = 3;

  #httpClient = inject(HttpClient);
  #geminiService = inject(GeminiService);
  #userService = inject(UserService);
  #wizardShellComponentService = inject(WizardShellComponentService);
  #destroyRef = inject(DestroyRef);
  #agencyGroup: Group | null = null;

  styles = FURNITURE_STYLE_OPTIONS;
  previewModes: PreviewModeOption[] = [
    { value: 'slider', label: 'Slider' },
    { value: 'side-by-side', label: 'Affiancato' },
    { value: 'tap', label: 'Tap' },
  ];

  fileControl = new FormControl<File[]>([], { nonNullable: true });
  isLoading = signal<boolean>(false);
  canAddCompanyLogo = signal<boolean>(false);

  addCompanyLogoControl = new FormControl<boolean>(true, { nonNullable: true });
  removeFurnitureControl = new FormControl<boolean>(true, {
    nonNullable: true,
  });
  removePersonalEffectsControl = new FormControl<boolean>(true, {
    nonNullable: true,
  });

  uiState = signal<UiState>('upload');
  furnitureStyle = signal<EFurnitureStyle | null>(null);

  processedImages = signal<ProcessedImage[]>([]);
  previewMode = signal<AbPreviewMode>('side-by-side');
  limitWarning = signal<string | null>(null);

  filesCount = signal<number>(0);

  hasActiveRemoveFurnitureOptions = computed(
    () =>
      this.removeFurnitureControl.value ||
      this.removePersonalEffectsControl.value
  );

  canProcess = computed(
    () =>
      this.filesCount() > 0 &&
      this.furnitureStyle() !== null &&
      (this.furnitureStyle() !== EFurnitureStyle.RIMUOVI_ARREDI ||
        this.hasActiveRemoveFurnitureOptions())
  );

  quotaExhausted = signal<boolean>(false);

  canDownload = computed(() =>
    this.processedImages().some((i) => i.status === 'done' && !!i.aiFile)
  );

  hasPending = computed(() =>
    this.processedImages().some(
      (i) => i.status === 'pending' || i.status === 'error'
    )
  );

  /** Una generazione (batch o singola) è in corso: usato per bloccare le azioni. */
  isGenerating = computed(() =>
    this.processedImages().some((i) => i.status === 'processing')
  );

  /** Conferma per azioni distruttive; null = nessun dialog aperto. */
  confirmDialog = signal<{
    title: string;
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
  } | null>(null);

  /** Solo questo account vede il box di debug del prompt nello step 3. */
  isPromptDebugEnabled = computed(() =>
    this.#userService.user?.email.includes('bitfdev')
  );

  /**
   * Anteprima live del prompt che verrà inviato a nanobanana per ogni foto,
   * ricalcolata a ogni cambio di config. Vuota se l'utente non è abilitato.
   */
  promptPreviews = computed<Record<number, string>>(() => {
    if (!this.isPromptDebugEnabled() || !this.furnitureStyle()) {
      return {};
    }
    const answers = this.#wizardAnswers();
    const previews: Record<number, string> = {};
    for (const img of this.processedImages()) {
      previews[img.index] = this.#geminiService.buildPrompt(
        answers,
        img.config
      );
    }
    return previews;
  });

  readonly stepItems = [
    {
      index: 1,
      state: 'upload' as const,
      eyebrow: 'Step 1',
      label: 'Foto',
      title: 'Carica il set fotografico',
      description:
        "Raccogli tutte le foto dell'immobile: più immagini includi, più completo e convincente sarà il pacchetto per il tuo cliente.",
    },
    {
      index: 2,
      state: 'configuring' as const,
      eyebrow: 'Step 2',
      label: 'Stile',
      title: 'Configura la staging AI',
      description:
        "Scegli lo stile di arredo che valorizza l'immobile e imposta il branding prima di avviare la trasformazione.",
    },
    {
      index: 3,
      state: 'generation' as const,
      eyebrow: 'Step 3',
      label: 'Generazione',
      title: 'Genera e scarica le immagini',
      description:
        'Elabora tutte le foto con un click oppure imposta condizioni specifiche per ogni foto. Confronta i risultati e scarica il pacchetto.',
    },
  ];

  currentStep = computed(
    () =>
      this.stepItems.find((step) => step.state === this.uiState()) ??
      this.stepItems[0]
  );

  constructor() {
    this.#wizardShellComponentService.activate();
    this.#destroyRef.onDestroy(() =>
      this.#wizardShellComponentService.deactivate()
    );

    effect(
      () => {
        const step = this.currentStep();
        this.#wizardShellComponentService.setStep({
          index: step.index,
          title: step.title,
          label: step.label,
          description: step.description,
        });
      },
      { allowSignalWrites: true }
    );

    this.filesCount.set(this.fileControl.value.length);

    // Fetch agency group info early so the "Applica logo agenzia" checkbox
    // is visible in step 3 without waiting for the download action.
    // The presigned S3 URL is refreshed again at download time.
    this.#refreshAgencyGroup();
  }

  onFilesChange(files: File[]) {
    this.fileControl.setValue(files);
    this.filesCount.set(files.length);

    if (!files.length && this.uiState() !== 'generation') {
      this.uiState.set('upload');
    } else if (files.length && this.uiState() === 'upload') {
      this.uiState.set('configuring');
    }
  }

  onUploadProceed() {
    if (!this.fileControl.value.length) {
      return;
    }

    this.uiState.set('configuring');
  }

  onBackToUpload() {
    if (this.#hasGeneratedWork()) {
      this.#requestConfirm({
        title: 'Tornare alle foto?',
        message:
          'Cambiando set di foto perderai tutte le immagini AI già generate. Le elaborazioni consumate non verranno restituite.',
        confirmLabel: 'Sì, torna alle foto',
        onConfirm: () => this.#performBackToUpload(),
      });
      return;
    }
    this.#performBackToUpload();
  }

  #performBackToUpload() {
    this.#revokeProcessedImages();
    this.processedImages.set([]);
    this.fileControl.setValue([]);
    this.filesCount.set(0);
    this.uiState.set('upload');
  }

  onFurnitureStyleChange(style: EFurnitureStyle) {
    const previousStyle = this.furnitureStyle();

    if (
      style === EFurnitureStyle.RIMUOVI_ARREDI &&
      previousStyle !== EFurnitureStyle.RIMUOVI_ARREDI
    ) {
      this.removePersonalEffectsControl.setValue(true);
      this.removeFurnitureControl.setValue(true);
    }

    this.furnitureStyle.set(style);
  }

  onAddCompanyLogoChange(value: boolean) {
    this.addCompanyLogoControl.setValue(value);
  }

  onRemoveFurnitureChange(value: boolean) {
    this.removeFurnitureControl.setValue(value);
  }

  onRemovePersonalEffectsChange(value: boolean) {
    this.removePersonalEffectsControl.setValue(value);
  }

  onRestart() {
    if (this.#hasGeneratedWork()) {
      this.#requestConfirm({
        title: 'Ricominciare da capo?',
        message:
          'Perderai tutte le immagini AI già generate e le elaborazioni consumate non verranno restituite.',
        confirmLabel: 'Sì, ricomincia',
        onConfirm: () => this.#performRestart(),
      });
      return;
    }
    this.#performRestart();
  }

  #performRestart() {
    this.#revokeProcessedImages();
    this.processedImages.set([]);
    this.limitWarning.set(null);
    this.furnitureStyle.set(null);
    this.removeFurnitureControl.setValue(true);
    this.removePersonalEffectsControl.setValue(true);
    this.fileControl.setValue([]);
    this.filesCount.set(0);
    this.uiState.set('upload');
  }

  /** C'è lavoro AI completato da proteggere prima di un'azione distruttiva? */
  #hasGeneratedWork(): boolean {
    return this.processedImages().some((i) => i.status === 'done');
  }

  #requestConfirm(config: NonNullable<ReturnType<typeof this.confirmDialog>>) {
    this.confirmDialog.set(config);
  }

  onConfirmDialog() {
    const config = this.confirmDialog();
    this.confirmDialog.set(null);
    config?.onConfirm();
  }

  onCancelDialog() {
    this.confirmDialog.set(null);
  }

  setPreviewMode(mode: AbPreviewMode) {
    this.previewMode.set(mode);
  }

  onProceedToGeneration() {
    if (!this.canProcess()) return;
    const files = this.fileControl.value ?? [];
    const existing = this.processedImages();
    const sameFiles =
      existing.length === files.length &&
      existing.every((img, i) => img.originalFile === files[i]);

    if (!sameFiles) {
      this.#revokeProcessedImages();
      this.processedImages.set(
        files.map((file, i) => ({
          index: i,
          originalFile: file,
          originalUrl: URL.createObjectURL(file),
          aiFile: null,
          aiUrl: null,
          prompt: '',
          status: 'pending' as const,
          config: createDefaultPhotoConfig(),
        }))
      );
    }

    this.limitWarning.set(null);
    this.#refreshQuotaState();
    this.uiState.set('generation');
  }

  onBackToStyle() {
    if (this.#hasGeneratedWork()) {
      this.#requestConfirm({
        title: 'Tornare allo stile?',
        message:
          'Modificando lo stile le immagini già generate potrebbero non essere più coerenti: dovrai rigenerarle per applicare il nuovo stile.',
        confirmLabel: 'Sì, torna allo stile',
        onConfirm: () => this.#performBackToStyle(),
      });
      return;
    }
    this.#performBackToStyle();
  }

  #performBackToStyle() {
    this.uiState.set('configuring');
  }

  onPhotoConfigChange(index: number, config: PhotoGenerationConfig) {
    this.#updateImage(index, { config });
  }

  #wizardAnswers(): WizardAnswers {
    return {
      furnitureStyle: this.furnitureStyle()!,
      luxuryLevel: ELuxuryLevel.ALTO,
      removeFurniture: this.removeFurnitureControl.value,
      removePersonalEffects: this.removePersonalEffectsControl.value,
      improveImages: true,
    };
  }

  #refreshQuotaState() {
    this.quotaExhausted.set(!this.#userService.hasRemainingAiImages());
  }

  async #decrementQuota(count: number) {
    try {
      await this.#userService.decrementRemainingAiImages(count);
    } catch (error) {
      console.error('AI image quota sync failed', error);
      this.limitWarning.set(
        'Le immagini sono state generate, ma non siamo riusciti ad aggiornare il numero di elaborazioni AI residue. Ricarica la pagina o riprova più tardi.'
      );
    } finally {
      this.#refreshQuotaState();
    }
  }

  async onGenerateOne(index: number) {
    const img = this.processedImages()[index];
    if (!img || img.status === 'processing') return;

    if (img.status === 'done') {
      return this.onRegenerate(index);
    }

    if (!this.#userService.hasRemainingAiImages()) {
      this.limitWarning.set(
        'Hai esaurito le elaborazioni AI disponibili. Non puoi generare altre immagini.'
      );
      this.#refreshQuotaState();
      return;
    }

    this.limitWarning.set(null);
    const prompt = this.#geminiService.buildPrompt(
      this.#wizardAnswers(),
      img.config
    );
    this.#updateImage(index, { prompt });
    const processed = await this.#processOne(index, prompt, undefined, {
      maxAttempts: this.#initialProcessMaxAttempts,
    });
    if (processed) {
      await this.#decrementQuota(1);
    }
  }

  async onProcessAll() {
    this.limitWarning.set(null);
    let targets = this.processedImages().filter(
      (i) => i.status === 'pending' || i.status === 'error'
    );
    const remaining = this.#userService.getRemainingAiImages();
    if (remaining !== undefined && targets.length > remaining) {
      this.limitWarning.set(
        `Hai raggiunto il limite di elaborazioni AI disponibili. Solo le prime ${remaining} immagini verranno elaborate.`
      );
      targets = targets.slice(0, remaining);
    }
    if (!targets.length) return;

    const results = await this.#runWithConcurrency(
      targets,
      this.#maxConcurrentGenerations,
      (img) => {
        const prompt = this.#geminiService.buildPrompt(
          this.#wizardAnswers(),
          img.config
        );
        this.#updateImage(img.index, { prompt });
        return this.#processOne(img.index, prompt, undefined, {
          maxAttempts: this.#initialProcessMaxAttempts,
        });
      }
    );
    const successfulCount = results.filter(Boolean).length;
    if (successfulCount > 0) {
      await this.#decrementQuota(successfulCount);
    }
  }

  async onRegenerate(index: number) {
    if (!this.#userService.hasRemainingAiImages()) {
      this.limitWarning.set(
        'Hai esaurito le elaborazioni AI disponibili. Non puoi rigenerare altre immagini.'
      );
      this.#refreshQuotaState();
      return;
    }

    const img = this.processedImages()[index];
    if (!img || img.status === 'processing') return;
    const fullPrompt =
      img.prompt +
      this.#geminiService.buildConsistencyFooter(this.#wizardAnswers());
    const processed = await this.#processOne(index, fullPrompt);
    if (!processed) {
      return;
    }
    await this.#decrementQuota(1);
  }

  async #refreshAgencyGroup(): Promise<void> {
    const user = await firstValueFrom(
      this.#httpClient.get<IUser>(`${environment.apiUrl}/users/me`, {
        params: {
          'populate[agentAgency][populate][0]': 'group',
          'populate[agentAgency][populate][group][populate][0]': 'companyLogo',
        },
      })
    );

    const group = user?.agentAgency?.group;
    if (!group?.documentId) {
      this.#agencyGroup = null;
      this.canAddCompanyLogo.set(false);
      return;
    }

    // Set canAddCompanyLogo immediately from the /users/me response so the
    // checkbox is visible in step 3 without waiting for the /gruppos call.
    this.canAddCompanyLogo.set(
      !!group.companyLogo &&
        !!group.companyLogoPosition &&
        !!group.companyLogoSizePercentage
    );

    // /users/me returns the raw companyLogo path; only /gruppos responds with
    // the presigned S3 URL, so the group must be fetched from there.
    const response = await firstValueFrom(
      this.#httpClient.get<{ data: Group }>(
        `${environment.apiUrl}/gruppos/${group.documentId}`,
        { params: { populate: 'companyLogo' } }
      )
    );
    this.#agencyGroup = response.data;
  }

  async onDownloadZip() {
    try {
      this.isLoading.set(true);
      // The company logo URL is a presigned S3 link that expires: fetch the
      // group right before stamping so long processing sessions don't fail.
      await this.#refreshAgencyGroup();
      const zip = new JSZip();
      const originalFolder = zip.folder('originali');
      const aiFolder = zip.folder('ai');

      for (const img of this.processedImages()) {
        if (img.status !== 'done' || !img.aiFile) continue;

        const originalExportFile = await this.#buildOriginalExportFile(
          img.originalFile
        );
        originalFolder?.file(img.originalFile.name, originalExportFile, {
          base64: true,
        });

        const stamped = await this.#applyAi(img.aiFile);
        aiFolder?.file(img.aiFile.name, stamped, { base64: true });
      }
      const zipFile = await zip.generateAsync({ type: 'blob' });
      promptToDownloadBlob(zipFile, 'zip', 'casa-doc-ai-' + Date.now());
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  async onEditImage(index: number, editPrompt: string) {
    if (!this.#userService.hasRemainingAiImages()) {
      this.limitWarning.set(
        'Hai esaurito le elaborazioni AI disponibili. Non puoi modificare altre immagini.'
      );
      return;
    }

    const img = this.processedImages()[index];
    if (!img?.aiFile || img.status === 'processing') return;

    const processed = await this.#processOne(
      index,
      this.#geminiService.buildEditPrompt(editPrompt),
      img.aiFile
    );
    if (!processed) return;
    await this.#decrementQuota(1);
  }

  async #processOne(
    index: number,
    prompt: string,
    sourceFile?: File,
    options?: { maxAttempts?: number }
  ): Promise<boolean> {
    const current = this.processedImages()[index];
    if (!current) return false;
    if (current.aiUrl) URL.revokeObjectURL(current.aiUrl);

    this.#updateImage(index, {
      status: 'processing',
      aiFile: null,
      aiUrl: null,
      errorMessage: undefined,
    });

    const maxAttempts = Math.max(1, options?.maxAttempts ?? 1);
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const aiFile = await this.#geminiService.enhanceImage(
          sourceFile ?? current.originalFile,
          prompt
        );
        this.#updateImage(index, {
          aiFile,
          aiUrl: URL.createObjectURL(aiFile),
          status: 'done',
        });
        return true;
      } catch (error) {
        lastError = error;
        // Errori non-recuperabili (blocco content-policy, 4xx) non vanno
        // ritentati: ripetere lo stesso input darebbe lo stesso esito.
        const retryable = error instanceof GeminiError ? error.retryable : true;
        if (!retryable || attempt >= maxAttempts) {
          break;
        }
        console.warn(
          `Image processing attempt ${attempt}/${maxAttempts} failed. Retrying...`,
          error
        );
        await this.#delay(this.#retryDelayMs);
      }
    }

    const message =
      lastError instanceof Error ? lastError.message : String(lastError);
    this.#updateImage(index, { status: 'error', errorMessage: message });
    console.error('Image processing failed', lastError);
    return false;
  }

  /**
   * Esegue i task con al massimo `limit` in volo contemporaneamente, così
   * "Genera tutte" non satura il modello con N richieste 2K simultanee.
   * Preserva l'ordine dei risultati rispetto a `items`.
   */
  async #runWithConcurrency<T, R>(
    items: T[],
    limit: number,
    task: (item: T) => Promise<R>
  ): Promise<R[]> {
    const results = new Array<R>(items.length);
    let cursor = 0;
    const workers = Array.from(
      { length: Math.min(limit, items.length) },
      async () => {
        while (cursor < items.length) {
          const current = cursor++;
          results[current] = await task(items[current]);
        }
      }
    );
    await Promise.all(workers);
    return results;
  }

  #delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async #applyAi(file: File): Promise<File> {
    if (!this.#shouldApplyAi()) {
      return file;
    }

    const group = this.#agencyGroup;
    const aiProcessor = await Ai.load(file);
    if (group?.casaDocLogoPosition) {
      await aiProcessor.applyImage({
        path: 'images/casadoc_ai_white_small.png',
        position: EAiPosition[group.casaDocLogoPosition],
        scale: 0.1,
        offset: 10,
      });
    }
    if (this.#canStampCompanyLogo() && group?.companyLogo) {
      await aiProcessor.applyImage({
        path: group.companyLogo.url,
        position: EAiPosition[group.companyLogoPosition],
        offsetScale: 0.02,
        scale: group.companyLogoSizePercentage / 100,
      });
    }
    return aiProcessor.getResult();
  }

  /** Il logo aziendale è stampabile solo se abilitato, scelto e configurato. */
  #canStampCompanyLogo(): boolean {
    return (
      this.canAddCompanyLogo() &&
      this.addCompanyLogoControl.value &&
      !!this.#agencyGroup?.companyLogo
    );
  }

  async #buildOriginalExportFile(file: File): Promise<File> {
    if (!this.#shouldApplyAi()) {
      return file;
    }

    return this.#applyAi(file);
  }

  #shouldApplyAi(): boolean {
    return (
      !!this.#agencyGroup?.casaDocLogoPosition || this.#canStampCompanyLogo()
    );
  }

  #updateImage(index: number, patch: Partial<ProcessedImage>) {
    this.processedImages.update((list) => {
      const next = [...list];
      const current = next[index];
      if (!current) return list;
      next[index] = { ...current, ...patch };
      return next;
    });
  }

  /** Rilascia tutti gli objectURL di originali e risultati AI per evitare leak. */
  #revokeProcessedImages() {
    for (const img of this.processedImages()) {
      URL.revokeObjectURL(img.originalUrl);
      if (img.aiUrl) URL.revokeObjectURL(img.aiUrl);
    }
  }
}
